import React from "react";

import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format } from "date-fns";
import { Controller, useForm } from "react-hook-form";
import { ByWeekday, Frequency, Options, RRule, Weekday } from "rrule";
import { makeStyles } from "tss-react/mui";

import { getNthDayOfWeek, monthday, weekday } from "@/utils/datetime";
import { muiField } from "@/utils/hookForm";
import { withOrdinal } from "@/utils/number";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (rule: RRule) => void;
  value?: string;
  sendsOn?: Date;
}

interface FormValues {
  interval: number;
  freq: Frequency;
  byweekday: Weekday[];
  monthlyBy: "weekday" | "monthday";
  monthlyByWeekday: ByWeekday;
  monthlyByMonthday: number;
  ends: "never" | "until" | "count";
  until: Date;
  count: number;
}

const useStyles = makeStyles()({
  frequency: {
    paddingLeft: 16,
  },
  weekday: {
    border: "1px solid #C8CED8 !important",
    borderRadius: "9999px !important",
    width: 36,
    height: 36,
    marginLeft: "0 !important",
    marginRight: 12,
    "&.Mui-selected": {
      backgroundColor: "#C8CED8",
    },
    "&.Mui-selected:hover": {
      backgroundColor: "rgba(200, 206, 216, 0.85)",
    },
  },
  rows: {
    "& > *:not(:last-child)": {
      marginBottom: 24,
    },
  },
  label: {
    display: "flex",
    alignItems: "center",
  },
  labelEnds: {
    alignItems: "flex-start",
    marginTop: 12,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    borderTop: "2px solid #DEE6F0",
    padding: "20px 0",
    "& > *:not(:last-child)": {
      marginRight: 10,
    },
  },
});

const CustomRecurrenceDialog: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  sendsOn = new Date(),
}) => {
  const { classes, cx } = useStyles();
  const { handleSubmit, control, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      interval: 1,
      freq: RRule.DAILY,
      byweekday: [] as ByWeekday[],
      monthlyBy: "weekday",
      monthlyByWeekday: undefined,
      monthlyByMonthday: undefined,
      ends: "never",
      until: sendsOn,
      count: 1,
    },
  });

  function submit(values: FormValues) {
    const options: Partial<Options> = {};
    options.freq = values.freq;
    options.interval = values.interval;

    switch (values.freq) {
      case RRule.WEEKLY:
        options.byweekday = values.byweekday;
        break;
      case RRule.MONTHLY:
        if (values.monthlyBy === "weekday") {
          options.byweekday = values.monthlyByWeekday;
        }
        if (values.monthlyBy === "monthday") {
          options.bymonthday = values.monthlyByMonthday;
        }
        break;
    }

    switch (values.ends) {
      case "until":
        options.until = values.until;
        break;
      case "count":
        options.count = values.count;
        break;
    }

    const rule = new RRule(options);
    onSubmit(rule);
    onClose();
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Custom recurrence</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit((values) => submit(values as FormValues))}>
          <Grid container className={classes.rows}>
            <Grid item container xs={12}>
              <Grid item xs={2} className={classes.label}>
                Repeat every
              </Grid>
              <Grid item xs={2}>
                <Controller
                  control={control}
                  name="interval"
                  render={({ field }) => (
                    <TextField
                      {...muiField(field)}
                      type="number"
                      inputProps={{ size: "3" }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={8}>
                <Controller
                  control={control}
                  name="freq"
                  render={({ field }) => (
                    <TextField
                      {...muiField(field)}
                      className={classes.frequency}
                      select
                      fullWidth
                    >
                      <MenuItem value={RRule.DAILY}>
                        {watch("interval") == 1 ? "Day" : "Days"}
                      </MenuItem>
                      <MenuItem value={RRule.WEEKLY}>
                        {watch("interval") == 1 ? "Week" : "Weeks"}
                      </MenuItem>
                      <MenuItem value={RRule.MONTHLY}>
                        {watch("interval") == 1 ? "Month" : "Months"}
                      </MenuItem>
                      <MenuItem value={RRule.YEARLY}>
                        {watch("interval") == 1 ? "Year" : "Years"}
                      </MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
            </Grid>
            {[RRule.WEEKLY, RRule.MONTHLY].includes(watch("freq")) && (
              <Grid item container xs={12}>
                <Grid item xs={2} className={classes.label}>
                  Repeat on
                </Grid>
                <Grid item xs={10}>
                  {watch("freq") === RRule.WEEKLY && (
                    <Controller
                      control={control}
                      name="byweekday"
                      render={({ field }) => (
                        <ToggleButtonGroup
                          {...muiField(field)}
                          onChange={(_, newValue) => field.onChange(newValue)}
                        >
                          <ToggleButton
                            className={classes.weekday}
                            value={RRule.SU}
                          >
                            Su
                          </ToggleButton>
                          <ToggleButton
                            className={classes.weekday}
                            value={RRule.MO}
                          >
                            M
                          </ToggleButton>
                          <ToggleButton
                            className={classes.weekday}
                            value={RRule.TU}
                          >
                            T
                          </ToggleButton>
                          <ToggleButton
                            className={classes.weekday}
                            value={RRule.WE}
                          >
                            W
                          </ToggleButton>
                          <ToggleButton
                            className={classes.weekday}
                            value={RRule.TH}
                          >
                            Th
                          </ToggleButton>
                          <ToggleButton
                            className={classes.weekday}
                            value={RRule.FR}
                          >
                            F
                          </ToggleButton>
                          <ToggleButton
                            className={classes.weekday}
                            value={RRule.SA}
                          >
                            S
                          </ToggleButton>
                        </ToggleButtonGroup>
                      )}
                    />
                  )}
                  {watch("freq") === RRule.MONTHLY && (
                    <Controller
                      control={control}
                      name="monthlyBy"
                      render={({ field }) => (
                        <TextField {...muiField(field)} select fullWidth>
                          <MenuItem
                            value="day"
                            onClick={() =>
                              setValue(
                                "monthlyByWeekday",
                                weekday(sendsOn).nth(getNthDayOfWeek(sendsOn))
                              )
                            }
                          >
                            The {withOrdinal(getNthDayOfWeek(sendsOn))}{" "}
                            {format(sendsOn, "EEEE")}
                          </MenuItem>
                          <MenuItem
                            value="monthDay"
                            onClick={() =>
                              setValue("monthlyByMonthday", monthday(sendsOn))
                            }
                          >
                            Day {sendsOn.getDate()}
                          </MenuItem>
                        </TextField>
                      )}
                    />
                  )}
                </Grid>
              </Grid>
            )}
            <Grid item container xs={12}>
              <Grid
                item
                xs={2}
                className={cx(classes.label, classes.labelEnds)}
              >
                Ends
              </Grid>
              <Grid item xs={10}>
                <Controller
                  control={control}
                  name="ends"
                  render={({ field }) => (
                    <RadioGroup {...field} row>
                      <FormControlLabel
                        value="never"
                        control={<Radio color="primary" />}
                        label="Never"
                      />
                      <FormControlLabel
                        value="until"
                        control={<Radio color="primary" />}
                        label="Specific date"
                      />
                      <FormControlLabel
                        value="count"
                        control={<Radio color="primary" />}
                        label="After no. of occurrences"
                      />
                    </RadioGroup>
                  )}
                />
                {watch("ends") === "until" && (
                  <Controller
                    control={control}
                    name="until"
                    render={({
                      field: { ref, name, onChange, ...field },
                      fieldState,
                    }) => (
                      <DatePicker
                        {...field}
                        inputRef={ref}
                        onChange={(value) => onChange(value as Date)}
                        disablePast
                        slotProps={{
                          textField: {
                            name,
                            fullWidth: true,
                            error: !!fieldState?.error,
                            helperText: fieldState?.error?.message,
                          },
                        }}
                      />
                    )}
                  />
                )}
                {watch("ends") === "count" && (
                  <Controller
                    control={control}
                    name="count"
                    render={({ field }) => (
                      <TextField
                        {...muiField(field)}
                        type="number"
                        fullWidth
                        required
                      />
                    )}
                  />
                )}
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <div className={classes.actions}>
                <Button variant="outlined" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit">Save</Button>
              </div>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CustomRecurrenceDialog;
